import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import { AuthContext } from "../../context/AuthContext";
import smartphoneIcon from "../../assets/icons/profile-page/smartphone.png";
import envelopeOpenIcon from "../../assets/icons/profile-page/envelope-open.png";
import angleRightIcon from "../../assets/icons/profile-page/angle-small-right.png";
import {
  setupAppTwoFactor,
  enableAppTwoFactor,
  enableEmailTwoFactor,
  disableAppTwoFactor,
  requestDisableEmailTwoFactor,
  disableEmailTwoFactor,
} from "../../services/UserService";
import { validateVerificationCodeInput } from "../../utils/verificationCodeErrorHandler";
import "./ProfileModals.css";

const CODE_LENGTH = 6;

const VIEWS = {
  OVERVIEW: "overview",
  SETUP_APP: "setup-app",
  DISABLE_APP: "disable-app",
  DISABLE_EMAIL: "disable-email",
};

const TwoFactorSettingsModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { twoFactorMethod, setTwoFactorMethod } = useContext(AuthContext);

  const TEXT = {
    title: t("profile.twoFactorTitle", "Two-Factor Authentication"),
    close: t("common.close", "Close"),
    intro: t(
      "profile.twoFactorIntro",
      "Two-factor authentication helps protect your account by requiring a second verification step when you sign in."
    ),
    status_label: t("profile.twoFactorStatusLabel", "Status"),
    status_enabled: t("profile.twoFactorStatusEnabled", "Enabled"),
    status_disabled: t("profile.twoFactorStatusDisabled", "Disabled"),
    enable_app_title: t("profile.twoFactorEnableAppTitle", "Authenticator app"),
    enable_app_desc: t(
      "profile.twoFactorEnableAppDesc",
      "Use a 6-digit code generated in your authentication app when signing in."
    ),
    enable_email_title: t("profile.twoFactorEnableEmailTitle", "Email verification"),
    enable_email_desc: t(
      "profile.twoFactorEnableEmailDesc",
      "Get a one-time verification code by email during sign-in."
    ),
    disable: t("profile.twoFactorDisable", "Disable"),
    back: t("common.back", "Back"),
    sending: t("profile.twoFactorSending", "Sending..."),
    verifying: t("profile.twoFactorVerifying", "Verifying..."),
    disabling: t("profile.twoFactorDisabling", "Disabling..."),
    disable_app_title: t("profile.twoFactorDisableAppTitle", "Disable authenticator app"),
    disable_app_intro: t(
      "profile.twoFactorDisableAppIntro",
      "To turn off authenticator app verification, enter the current 6-digit code from your app."
    ),
    disable_email_title: t("profile.twoFactorDisableEmailTitle", "Disable email verification"),
    disable_email_intro: t(
      "profile.twoFactorDisableEmailIntro",
      "We sent a verification code to your email address. Enter it below to disable email verification."
    ),
    disable_email_code_label: t("profile.twoFactorDisableEmailCodeLabel", "Verification code"),
    qr_alt: t("profile.twoFactorQrAlt", "Authenticator app QR code"),
    digit_label: (index) => t("auth.digitLabel", "Digit {{index}}", { index }),
    error_generic: t("common.errorGeneric", "Something went wrong. Please try again."),
    setup_app_title: t("profile.twoFactorSetupAppTitle", "Set up authenticator app"),
    setup_app_hint: t("profile.twoFactorSetupAppHint", "Enter the 6-digit code from your authenticator app."),
    setup_app_step_scan: t(
      "profile.twoFactorSetupAppStepScan",
      "Scan this QR code in your authentication app."
    ),
    setup_app_step_code: t(
      "profile.twoFactorSetupAppStepCode",
      "Enter the code currently shown for this account in your authentication app."
    ),
    enable: t("profile.twoFactorEnable", "Enable"),
    active_app_title: t("profile.twoFactorActiveAppTitle", "Authenticator app"),
    active_email_title: t("profile.twoFactorActiveEmailTitle", "Email verification"),
    enabled_app_desc: t(
      "profile.twoFactorEnabledAppDesc",
      "Your account is protected with codes from your authenticator app."
    ),
    enabled_email_desc: t(
      "profile.twoFactorEnabledEmailDesc",
      "Your account is protected with codes sent to your email."
    ),
    change_to_email_note: t(
      "profile.twoFactorChangeToEmailNote",
      "To switch to email verification, first disable your current 2FA method."
    ),
    change_to_app_note: t(
      "profile.twoFactorChangeToAppNote",
      "To switch to an authenticator app, first disable your current method."
    ),
  };

  const [view, setView] = useState(VIEWS.OVERVIEW);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [codeValues, setCodeValues] = useState(Array(CODE_LENGTH).fill(""));
  const [selectedMethod, setSelectedMethod] = useState(null);

  const inputRefs = useRef([]);
  const code = codeValues.join("");

  useEffect(() => {
    if (isOpen) {
      setView(VIEWS.OVERVIEW);
      setError("");
      setSuccessMessage("");
      setCodeValues(Array(CODE_LENGTH).fill(""));
      setSelectedMethod(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const shouldFocusCode = view === VIEWS.SETUP_APP || view === VIEWS.DISABLE_APP || view === VIEWS.DISABLE_EMAIL;

    if (isOpen && shouldFocusCode) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 0);
    }
  }, [isOpen, view]);

  const resetCodeState = () => {
    setCodeValues(Array(CODE_LENGTH).fill(""));
    setError("");
  };

  const cleanupQrCode = () => {
    if (qrCode) {
      URL.revokeObjectURL(qrCode);
      setQrCode("");
    }
  };

  const goToOverview = () => {
    cleanupQrCode();
    setView(VIEWS.OVERVIEW);
    setSuccessMessage("");
    resetCodeState();
    setSelectedMethod(null);
  };

  const handleClose = () => {
    if (loading) return;
    goToOverview();
    onClose();
  };

  const handleBack = () => {
    if (loading) return;
    goToOverview();
  };

  const parseError = (err) => {
    const detail = err?.response?.data?.detail;

    if (!detail) {
      return TEXT.error_generic;
    }

    if (typeof detail === "string") {
      return detail;
    }

    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          if (typeof item === "string") return item;
          if (item?.msg) return item.msg;
          return JSON.stringify(item);
        })
        .join(", ");
    }

    if (typeof detail === "object") {
      if (detail.msg) return detail.msg;
      return JSON.stringify(detail);
    }

    return TEXT.error_generic;
  };

  const handleOpenAppSetup = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      const imageBlob = await setupAppTwoFactor();
      const imageUrl = URL.createObjectURL(imageBlob);

      cleanupQrCode();
      setQrCode(imageUrl);
      resetCodeState();
      setView(VIEWS.SETUP_APP);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEmail = () => {
    setError("");
    setSelectedMethod("email");
  };

  const handleEnableApp = async () => {
    const validationError = validateVerificationCodeInput(code);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError("");
      await enableAppTwoFactor(code);
      setTwoFactorMethod?.("app");
      goToOverview();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEnableEmail = async () => {
    try {
      setLoading(true);
      setError("");
      await enableEmailTwoFactor();
      setTwoFactorMethod?.("email");
      goToOverview();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDisableApp = () => {
    resetCodeState();
    setView(VIEWS.DISABLE_APP);
  };

  const handleDisableApp = async () => {
    const validationError = validateVerificationCodeInput(code);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError("");
      await disableAppTwoFactor(code);
      setTwoFactorMethod?.(null);
      goToOverview();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDisableEmail = async () => {
    try {
      setLoading(true);
      setError("");
      await requestDisableEmailTwoFactor();
      resetCodeState();
      setView(VIEWS.DISABLE_EMAIL);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDisableEmail = async () => {
    const validationError = validateVerificationCodeInput(code);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError("");
      await disableEmailTwoFactor(code);
      setTwoFactorMethod?.(null);
      goToOverview();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, rawValue) => {
    const next = [...codeValues];
    next[index] = rawValue.slice(-1);
    setCodeValues(next);
    setError("");

    if (rawValue && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    const allowedControlKeys = ["Backspace", "Delete", "Tab", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"];

    if (allowedControlKeys.includes(e.key)) {
      if (e.key === "Backspace") {
        if (!codeValues[index]) {
          if (index > 0) {
            inputRefs.current[index - 1]?.focus();
          }
          return;
        }

        const next = [...codeValues];
        next[index] = "";
        setCodeValues(next);
        setError("");
        return;
      }

      if (e.key === "Delete") {
        const next = [...codeValues];
        next[index] = "";
        setCodeValues(next);
        setError("");
        return;
      }

      if (e.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
        return;
      }

      if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
        return;
      }

      return;
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();

    const pasted = e.clipboardData.getData("text").slice(0, CODE_LENGTH);

    if (!pasted) return;

    const next = Array(CODE_LENGTH).fill("");
    pasted.split("").forEach((char, idx) => {
      if (idx < CODE_LENGTH) {
        next[idx] = char;
      }
    });

    setCodeValues(next);
    setError("");
    inputRefs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  };

  const renderOtpInputs = () => (
    <div className="two-factor-otp-group" onPaste={handleCodePaste}>
      {codeValues.map((char, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          className="two-factor-otp-input"
          value={char}
          onChange={(e) => handleCodeChange(index, e.target.value)}
          onKeyDown={(e) => handleCodeKeyDown(index, e)}
          onFocus={(e) => e.target.select()}
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          aria-label={TEXT.digit_label(index + 1)}
          disabled={loading}
        />
      ))}
    </div>
  );

  const modalTitle =
    view === VIEWS.SETUP_APP
      ? TEXT.setup_app_title
      : view === VIEWS.DISABLE_APP
        ? TEXT.disable_app_title
        : view === VIEWS.DISABLE_EMAIL
          ? TEXT.disable_email_title
          : TEXT.title;

  const modalActions =
    view === VIEWS.OVERVIEW ? (
      <div className="two-factor-modal-actions">
        <SecondButton onClick={handleClose} disabled={loading}>
          {TEXT.close}
        </SecondButton>
      </div>
    ) : (
      <div className="two-factor-modal-actions two-factor-modal-actions--split">
        <SecondButton onClick={handleBack} disabled={loading}>
          {TEXT.back}
        </SecondButton>

        {view === VIEWS.SETUP_APP && (
          <MainButton onClick={handleEnableApp} disabled={loading}>
            {loading ? TEXT.verifying : TEXT.enable}
          </MainButton>
        )}

        {view === VIEWS.DISABLE_APP && (
          <MainButton onClick={handleDisableApp} disabled={loading}>
            {loading ? TEXT.disabling : TEXT.disable}
          </MainButton>
        )}

        {view === VIEWS.DISABLE_EMAIL && (
          <MainButton onClick={handleDisableEmail} disabled={loading}>
            {loading ? TEXT.verifying : TEXT.disable}
          </MainButton>
        )}
      </div>
    );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={modalTitle} actions={modalActions}>
      <div className="two-factor-modal">
        {view === VIEWS.OVERVIEW && (
          <>
            <p className="two-factor-modal__intro">{TEXT.intro}</p>

            <div className="two-factor-status-card">
              <div className="two-factor-status-card__top">
                <span className="two-factor-status-card__label">{TEXT.status_label}</span>
                <span className={`two-factor-status-badge ${twoFactorMethod ? "is-enabled" : "is-disabled"}`}>
                  {twoFactorMethod ? TEXT.status_enabled : TEXT.status_disabled}
                </span>
              </div>
            </div>

            {!twoFactorMethod && !selectedMethod && (
              <div className="two-factor-methods">
                <button type="button" className="two-factor-method-tile" onClick={handleOpenAppSetup}>
                  <div className="two-factor-method-tile__left">
                    <div className="two-factor-method-tile__content">
                      <div className="two-factor-method-tile__heading">
                        <img src={smartphoneIcon} alt="" className="two-factor-method-tile__icon" />
                        <p className="two-factor-method-tile__title">{TEXT.enable_app_title}</p>
                      </div>
                      <p className="two-factor-method-tile__desc">{TEXT.enable_app_desc}</p>
                    </div>
                  </div>
                  <img src={angleRightIcon} alt="" className="two-factor-method-tile__arrow" />
                </button>

                <button type="button" className="two-factor-method-tile" onClick={handleSelectEmail}>
                  <div className="two-factor-method-tile__left">
                    <div className="two-factor-method-tile__content">
                      <div className="two-factor-method-tile__heading">
                        <img src={envelopeOpenIcon} alt="" className="two-factor-method-tile__icon" />
                        <p className="two-factor-method-tile__title">{TEXT.enable_email_title}</p>
                      </div>
                      <p className="two-factor-method-tile__desc">{TEXT.enable_email_desc}</p>
                    </div>
                  </div>
                  <img src={angleRightIcon} alt="" className="two-factor-method-tile__arrow" />
                </button>
              </div>
            )}

            {!twoFactorMethod && selectedMethod === "email" && (
              <div className="two-factor-active-method">
                <div className="two-factor-method-tile two-factor-method-tile--active">
                  <div className="two-factor-method-tile__left">
                    <div className="two-factor-method-tile__content">
                      <div className="two-factor-method-tile__heading">
                        <img src={envelopeOpenIcon} alt="" className="two-factor-method-tile__icon" />
                        <p className="two-factor-method-tile__title">{TEXT.enable_email_title}</p>
                      </div>

                      <p className="two-factor-method-tile__desc">{TEXT.enable_email_desc}</p>
                    </div>
                  </div>

                  <MainButton onClick={handleEnableEmail} disabled={loading}>
                    {loading ? TEXT.verifying : TEXT.enable}
                  </MainButton>
                </div>

                <p className="two-factor-change-note">{TEXT.change_to_app_note}</p>
              </div>
            )}

            {twoFactorMethod && (
              <div className="two-factor-active-method">
                <div className="two-factor-method-tile two-factor-method-tile--active">
                  <div className="two-factor-method-tile__left">
                    <div className="two-factor-method-tile__content">
                      <div className="two-factor-method-tile__heading">
                        <img
                          src={twoFactorMethod === "app" ? smartphoneIcon : envelopeOpenIcon}
                          alt=""
                          className="two-factor-method-tile__icon"
                        />
                        <p className="two-factor-method-tile__title">
                          {twoFactorMethod === "app" ? TEXT.active_app_title : TEXT.active_email_title}
                        </p>
                      </div>

                      <p className="two-factor-method-tile__desc">
                        {twoFactorMethod === "app" ? TEXT.enabled_app_desc : TEXT.enabled_email_desc}
                      </p>
                    </div>
                  </div>

                  <MainButton
                    onClick={twoFactorMethod === "app" ? handleOpenDisableApp : handleOpenDisableEmail}
                    disabled={loading}
                  >
                    {loading && twoFactorMethod === "email" ? TEXT.sending : TEXT.disable}
                  </MainButton>
                </div>

                <p className="two-factor-change-note">
                  {twoFactorMethod === "app" ? TEXT.change_to_email_note : TEXT.change_to_app_note}
                </p>
              </div>
            )}
          </>
        )}

        {view === VIEWS.SETUP_APP && (
          <div className="two-factor-step">
            {qrCode && (
              <div className="two-factor-qr-card">
                <img src={qrCode} alt={TEXT.qr_alt} className="two-factor-qr-image" />
              </div>
            )}

            <div className="two-factor-instructions">
              <div className="two-factor-instruction-item">
                <span className="two-factor-instruction-step">1.</span>
                <p className="two-factor-instruction-text">{TEXT.setup_app_step_scan}</p>
              </div>

              <div className="two-factor-instruction-item">
                <span className="two-factor-instruction-step">2.</span>
                <p className="two-factor-instruction-text">{TEXT.setup_app_step_code}</p>
              </div>
            </div>

            <div className="two-factor-code-section">
              {renderOtpInputs()}
              <p className="two-factor-modal-hint two-factor-modal-hint--center">{TEXT.setup_app_hint}</p>
            </div>
          </div>
        )}

        {view === VIEWS.DISABLE_APP && (
          <div className="two-factor-step">
            <p className="two-factor-modal__intro">{TEXT.disable_app_intro}</p>
            {renderOtpInputs()}
          </div>
        )}

        {view === VIEWS.DISABLE_EMAIL && (
          <div className="two-factor-step">
            <p className="two-factor-modal__intro">{TEXT.disable_email_intro}</p>
            {renderOtpInputs()}
          </div>
        )}

        {error && (
          <div className="two-factor-modal-error">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {successMessage && <div className="profile-modal-success-wrapper">{successMessage}</div>}
      </div>
    </Modal>
  );
};

export default TwoFactorSettingsModal;
